import { useEffect, useMemo, useRef, useState } from "react";
import type { Stage as KonvaStage } from "konva/lib/Stage";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router";
import { FormattedMessage, useIntl } from "react-intl";
import {
  AlignCenter,
  AlignCenterVertical,
  AlignEndVertical,
  AlignJustify,
  AlignLeft,
  AlignRight,
  AlignStartVertical,
  ArrowLeft,
  Bold,
  BringToFront,
  Eye,
  EyeOff,
  Frame,
  ImageIcon,
  Italic,
  Loader2,
  MoveDown,
  MoveUp,
  Save,
  SendToBack,
  Shapes,
  Square,
  Trash2,
  Type,
  UploadCloud,
  X,
  type LucideIcon,
} from "lucide-react";
import { toast } from "sonner";
import {
  applySocialPostTemplateEditBatch,
  socialPostEditableDraftSchema,
  type SocialPostEditableDraft,
  type SocialPostFontStyle,
  type SocialPostImageRegenerationTaskRequestV1,
  type SocialPostSceneBlockV1,
  type SocialPostScenePlanV1,
  type SocialPostShapeType,
  type SocialPostTemplateEditOperation,
  type SocialPostTextAlign,
  type SocialPostVerticalAlign,
} from "@runcheetah/shared";

import {
  applyCreativeShapePreset,
  createDefaultCreativeLayerBlock,
  getNextRootLayerZIndex,
  type CreativeLayerCreateKind,
} from "@/components/campaign/campaign-creative-layer-factory";
import {
  CreativeInspectorLabel as Label,
  CreativeInspectorNumberField as InspectorNumberField,
  CreativeInspectorSelectField as InspectorSelectField,
  CreativeInspectorSection as InspectorSection,
  CreativeInspectorSliderField as InspectorSliderField,
  CreativeLayerAddMenu as AddLayerMenu,
  CreativeLayerSortableRow,
  CreativeRectangleSizePresetControl as RectangleSizePresetControl,
} from "@/components/campaign/creative-layer-editor-controls";
import {
  CreativeInspectorAdvancedGeometry,
  CreativeInspectorColorField,
  CreativeInspectorCornerRadiusField,
  CreativeInspectorFitField,
  CreativePostHashtagInput,
} from "@/components/campaign/creative-layer-inspector-fields";
import {
  createCreativeLayerCompactionUpdates,
  createCreativeLayerMoveEdgeUpdates,
  createCreativeLayerMoveStepUpdates,
  createCreativeLayerReorderUpdates,
  getSortedCreativeLayerEntries,
  getVisibleCreativeLayerEntries,
  type CreativeLayerEntry,
} from "@/components/campaign/campaign-creative-layer-ordering";
import {
  canRemoveSelectedLayer,
  createBlockEditOperation,
  createLayerId,
  getCustomLayerName,
  truncateLayerLabel,
  updateBlockInPlan,
} from "@/components/campaign/creative-layer-editor-utils";
import {
  CampaignCreativeScenePreview,
  type CampaignCreativeLinkedAsset,
} from "@/components/campaign/campaign-creative-scene-preview";
import { PostPreviewOverflowMenu } from "@/components/campaign/post-preview-overflow-menu";
import {
  CreativeLogoPicker,
  type CreativeLogoOption,
} from "@/components/campaign/creative-logo-picker";
import { CreativeEditorStudio } from "@/components/campaign/creative-editor-studio";
import { CreativeLayersPanel } from "@/components/campaign/creative-layers-popover";
import { CenteredLoader, EmptyCard } from "@/components/common";
import { RunConfirmationDialog } from "@/components/runs/run-confirmation-dialog";
import { useAssetCreationGate } from "@/hooks/use-asset-creation-gate";
import { buildBrandPath, getUnavailableBrandPath, useRouteBrand } from "@/hooks/use-route-brand";
import { downloadSocialPostPreviewImage } from "@/lib/social-post-preview-download";
import { normalizeSocialPostDraftLogoAssetKeys } from "@/lib/social-post-draft-logos";
import { mergeActiveSocialPostEditDraft } from "@/lib/social-post-editor-draft";
import { getCarouselArtboardSelectionBlockId } from "@/lib/carousel-editor-helpers";
import { type ImageFitValue } from "@/lib/image-fit-options";
import {
  hasActiveImageRegenerationForPost,
  normalizeStandaloneScheduledPosts,
} from "@/lib/campaign-posts";
import { hasActiveInstagramPublishNow } from "@/lib/instagram-publication";
import { canPublishPostNow } from "@/lib/post-publish-now";
import {
  EMPTY_POST_DRAFT_HISTORY,
  pushPostDraftHistory,
  redoPostDraftHistory,
  undoPostDraftHistory,
} from "@/lib/post-draft-history";
import { getPostDraftHydrationAction } from "@/lib/post-draft-sync";
import {
  applySocialPostVariantDraft,
  upsertSocialPostVariantDraft,
} from "@/lib/social-post-variant-drafts";
import { trpc } from "@/lib/trpc";
import { Button, SocialPostSceneRenderer, Textarea } from "@runcheetah/ui";
import {
  getStandalonePostBaselineDraft,
  getStandalonePostDraftState,
} from "./standalone-post-state";

type StandalonePostPageMode = "page" | "focused";
type FocusedEditorSelectionState = {
  blockId: string | null;
  generatedAssetId: string | null;
  slideId: string | null;
  slideIndex: number | null;
  asset: CampaignCreativeLinkedAsset | null;
};
type PartialImageRegenerationRequest = Extract<
  SocialPostImageRegenerationTaskRequestV1,
  { mode: "partial" }
>;

const BRAND_PALETTE_COLOR_RE = /^#[0-9a-f]{6}$/i;

function getBrandPrimaryPaletteColors(colors: string[] | null | undefined) {
  const seen = new Set<string>();
  const palette: string[] = [];

  for (const rawColor of colors ?? []) {
    const color = rawColor.trim().toUpperCase();
    const key = color.toLowerCase();

    if (!BRAND_PALETTE_COLOR_RE.test(color) || seen.has(key)) {
      continue;
    }

    seen.add(key);
    palette.push(color);
  }

  return palette;
}

function StandalonePostPage({ mode = "page" }: { mode?: StandalonePostPageMode } = {}) {
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const { batchId, postId } = useParams<{ batchId: string; postId: string }>();
  const { brand, brandQuery } = useRouteBrand();
  const assetCreationGate = useAssetCreationGate(brand?.id);
  const isFocusedMode = mode === "focused";
  const hasBackgroundLocation = hasBackgroundLocationState(location.state);
  const focusedEditorSelection = useMemo(
    () => getFocusedEditorSelectionState(location.state),
    [location.state],
  );
  const utils = trpc.useUtils();
  const [draft, setDraft] = useState<SocialPostEditableDraft | null>(null);
  const [downloadingImage, setDownloadingImage] = useState(false);
  const downloadStageRef = useRef<KonvaStage | null>(null);
  const [selectedCarouselSlideId, setSelectedCarouselSlideId] = useState<string | null>(null);
  const [carouselToolbarHost, setCarouselToolbarHost] = useState<HTMLDivElement | null>(null);
  const [carouselWorkspaceZoom, setCarouselWorkspaceZoom] = useState(1);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [selectedBlockIds, setSelectedBlockIds] = useState<string[]>([]);
  const didClearSelectedBlockRef = useRef(false);
  const didApplyFocusedSelectionRef = useRef(false);
  const [draftHistory, setDraftHistory] = useState(EMPTY_POST_DRAFT_HISTORY);
  const [draftWarning, setDraftWarning] = useState<string | null>(null);
  const hydratedPostIdRef = useRef<string | null>(null);
  const lastServerDraftKeyRef = useRef<string | null>(null);
  const [showLayersDrawer, setShowLayersDrawer] = useState(false);
  const [inspectorTab, setInspectorTab] = useState<"layers" | "caption">("layers");
  const [pendingImageRegenerationRequest, setPendingImageRegenerationRequest] =
    useState<PartialImageRegenerationRequest | null>(null);
  const imageRegenerationResolveRef = useRef<((accepted: boolean) => void) | null>(null);

  const batchQuery = trpc.standaloneAssetBatch.get.useQuery(
    { brandId: brand?.id ?? "", id: batchId ?? "" },
    {
      enabled: Boolean(brand?.id && batchId),
      refetchOnWindowFocus: true,
      refetchInterval: (query) =>
        hasActiveImageRegenerationForPost(query.state.data?.scheduledPosts, postId) ||
        hasActiveInstagramPublishNow(query.state.data?.scheduledPosts, postId)
          ? 2_000
          : false,
    },
  );
  const logosQuery = trpc.brand.listLogos.useQuery(
    { brandId: brand?.id ?? "" },
    { enabled: Boolean(brand?.id) },
  );
  const visualStyleQuery = trpc.brand.getVisualStyle.useQuery(
    { brandId: brand?.id ?? "" },
    { enabled: Boolean(brand?.id) },
  );
  const createLogoUpload = trpc.brand.createLogoUpload.useMutation();
  const completeLogoUpload = trpc.brand.completeLogoUpload.useMutation();
  const deleteLogo = trpc.brand.deleteLogo.useMutation();
  const saveDraftMutation = trpc.standaloneAssetBatch.upsertPostDraft.useMutation({
    onSuccess: async (data, variables) => {
      utils.standaloneAssetBatch.get.setData(
        { brandId: variables.brandId, id: variables.batchId },
        (current) => {
          if (!current) {
            return current;
          }

          return {
            ...current,
            scheduledPosts: current.scheduledPosts.map((scheduledPost) =>
              scheduledPost.id === variables.scheduledPostId
                ? {
                    ...scheduledPost,
                    draft: data.draft,
                    draftUpdatedAt: data.updatedAt,
                    hasUnpublishedDraft: data.draft
                      ? JSON.stringify(data.draft) !== JSON.stringify(scheduledPost.publishedDraft)
                      : false,
                  }
                : scheduledPost,
            ),
          };
        },
      );
      if (variables.brandId && variables.batchId) {
        await Promise.all([
          utils.brandAssistantChat.generatedAssets.invalidate(),
          utils.calendar.list.invalidate(),
        ]);
      }
    },
    onError: (_error, variables) => {
      void utils.standaloneAssetBatch.get.invalidate({
        brandId: variables.brandId,
        id: variables.batchId,
      });
    },
  });
  const publishDraftMutation = trpc.standaloneAssetBatch.publishPostDraft.useMutation({
    onSuccess: async () => {
      if (brand?.id && batchId) {
        await Promise.all([
          utils.standaloneAssetBatch.get.invalidate({ brandId: brand.id, id: batchId }),
          utils.brandAssistantChat.generatedAssets.invalidate(),
          utils.calendar.list.invalidate(),
        ]);
      }
    },
  });
  const publishNowMutation = trpc.standaloneAssetBatch.publishNowInstagram.useMutation({
    onSuccess: async () => {
      if (brand?.id && batchId) {
        await Promise.all([
          utils.standaloneAssetBatch.get.invalidate({ brandId: brand.id, id: batchId }),
          utils.brandAssistantChat.generatedAssets.invalidate(),
          utils.calendar.list.invalidate(),
        ]);
      }
      toast.success(
        intl.formatMessage({
          id: "standalonePost.publishNowStarted",
          defaultMessage: "Instagram publishing started.",
        }),
      );
    },
  });
  const regenerateImageMutation = trpc.standaloneAssetBatch.regeneratePostImage.useMutation({
    onSuccess: async () => {
      if (brand?.id && batchId) {
        await utils.standaloneAssetBatch.get.invalidate({ brandId: brand.id, id: batchId });
        await utils.brand.getBillingOverview.invalidate({ brandId: brand.id });
      }
    },
  });
  const imageRegenerationQuoteQuery = trpc.standaloneAssetBatch.quoteRegeneratePostImage.useQuery(
    {
      brandId: brand?.id ?? "00000000-0000-0000-0000-000000000000",
      batchId: batchId ?? "00000000-0000-0000-0000-000000000000",
    },
    { enabled: Boolean(brand?.id && batchId && pendingImageRegenerationRequest), retry: false },
  );

  const batch = batchQuery.data ?? null;
  const posts = useMemo(
    () => normalizeStandaloneScheduledPosts(batch?.scheduledPosts),
    [batch?.scheduledPosts],
  );
  const post = posts.find((candidate) => candidate.id === postId) ?? null;
  const latestCompleted = post?.output.latestCompleted ?? null;
  const brandkit = latestCompleted?.preview?.brandkit ?? null;
  const logoOptions = useMemo(() => {
    const options: CreativeLogoOption[] = [];
    const uploadedLogos = logosQuery.data?.logos ?? [];
    if (uploadedLogos.length === 0 && brandkit?.logo) {
      options.push({
        id: `preview:${brandkit.logo.objectKey}`,
        objectKey: brandkit.logo.objectKey,
        signedUrl: brandkit.logo.signedUrl,
        label: intl.formatMessage({
          id: "standalonePost.logoPicker.currentLogo",
          defaultMessage: "Primary logo",
        }),
      });
    }

    for (const logo of uploadedLogos) {
      options.push({
        id: logo.id,
        objectKey: logo.objectKey,
        signedUrl: logo.signedUrl,
        label: logo.label,
        isPrimary: logo.isPrimary,
      });
    }

    return options;
  }, [brandkit?.logo, intl, logosQuery.data?.logos]);
  const logoAssets = useMemo(
    () =>
      (logosQuery.data?.logos ?? []).map((logo) => ({
        objectKey: logo.objectKey,
        signedUrl: logo.signedUrl,
        isPrimary: logo.isPrimary,
      })),
    [logosQuery.data?.logos],
  );
  const brandPrimaryColors = useMemo(
    () => getBrandPrimaryPaletteColors(visualStyleQuery.data?.colorPalette),
    [visualStyleQuery.data?.colorPalette],
  );
  const carouselSlides = useMemo(
    () => latestCompleted?.carousel?.slides ?? [],
    [latestCompleted?.carousel?.slides],
  );
  const isCarouselPost = carouselSlides.length > 0;
  const generatedMetadata =
    post?.output.latestCopy?.result?.metadata ??
    latestCompleted?.result?.metadata ??
    post?.output.latestAttempt?.result?.metadata;
  const baselineCarouselSlide = carouselSlides[0] ?? null;
  const baselinePlan =
    latestCompleted?.result?.plan ?? baselineCarouselSlide?.scenePlan?.plan ?? null;
  const baselineCaption = generatedMetadata?.caption ?? latestCompleted?.carousel?.caption ?? null;
  const baselineHashtags = generatedMetadata?.hashtags ?? latestCompleted?.carousel?.hashtags;
  const baselineDraft = useMemo(() => {
    const persistedDraft = post?.draft ?? null;
    const shouldUsePersistedDraft = shouldUsePersistedDraftForFocusedSelection({
      draft: persistedDraft,
      selection: focusedEditorSelection,
    });
    const resolvedDraft = getStandalonePostBaselineDraft({
      persistedDraft: shouldUsePersistedDraft ? persistedDraft : null,
      copyDraft: shouldUsePersistedDraft ? null : persistedDraft,
      baselinePlan,
      baselineCaption,
      baselineHashtags,
      carouselSlides,
    });

    return applyFocusedGeneratedAssetSelectionToDraft({
      draft: resolvedDraft,
      persistedDraft,
      selection: focusedEditorSelection,
    });
  }, [
    baselineCaption,
    baselineHashtags,
    baselinePlan,
    carouselSlides,
    focusedEditorSelection,
    post?.draft,
  ]);
  const activeCarouselSlide =
    carouselSlides.find((slideOutput) => slideOutput.slide.id === selectedCarouselSlideId) ??
    carouselSlides[0] ??
    null;
  const activeCarouselSlideDraft = activeCarouselSlide
    ? (draft?.carouselSlides.find(
        (slideDraft) => slideDraft.slideId === activeCarouselSlide.slide.id,
      ) ?? null)
    : null;
  const activePlan = activeCarouselSlideDraft?.plan ?? draft?.plan ?? null;
  const canAddLogoLayer = !activePlan?.scene.blocks.some((block) => block.type === "logo");
  const activeLinkedAssets = useMemo(
    () =>
      applyFocusedLinkedAssetSelection({
        linkedAssets: activeCarouselSlide?.assets ?? latestCompleted?.assets ?? [],
        selection: focusedEditorSelection,
      }),
    [activeCarouselSlide?.assets, focusedEditorSelection, latestCompleted?.assets],
  );
  const currentDraftKey = useMemo(() => JSON.stringify(draft), [draft]);
  const baselineDraftKey = useMemo(() => JSON.stringify(baselineDraft), [baselineDraft]);
  const persistedDraftKey = useMemo(() => JSON.stringify(post?.draft ?? null), [post?.draft]);
  const publishedDraftKey = useMemo(
    () => JSON.stringify(post?.publishedDraft ?? null),
    [post?.publishedDraft],
  );
  const isPostEditable = post?.approvalStatus !== "approved";
  const hasPersistedDraft = Boolean(post?.draft);
  const draftState = useMemo(
    () =>
      getStandalonePostDraftState({
        currentDraftKey,
        baselineDraftKey,
        persistedDraftKey,
        publishedDraftKey,
        hasPersistedDraft,
        isPostEditable,
        isSavingDraft: saveDraftMutation.isPending,
        isPublishingDraft: publishDraftMutation.isPending,
      }),
    [
      baselineDraftKey,
      currentDraftKey,
      hasPersistedDraft,
      isPostEditable,
      persistedDraftKey,
      publishedDraftKey,
      publishDraftMutation.isPending,
      saveDraftMutation.isPending,
    ],
  );
  const { hasUnsavedChanges } = draftState;
  const canPublishNow = post
    ? canPublishPostNow({
        postStatus: post.status,
        approvalStatus: post.approvalStatus,
        postType: post.slot.postType,
        instagramConnected: post.instagramConnection?.isConnected === true,
        publication: post.publication,
        isPublishing: publishNowMutation.isPending,
      })
    : false;
  const shouldShowPublishNow = canPublishNow || publishNowMutation.isPending;
  const availableFontFamilies = useMemo(
    () => activePlan?.scene.fonts.map((font) => font.family) ?? [],
    [activePlan],
  );
  const layerEntries = useMemo(() => getSortedCreativeLayerEntries(activePlan), [activePlan]);
  const editableBlocks = useMemo(() => layerEntries.map((entry) => entry.block), [layerEntries]);
  const visibleLayerEntries = useMemo(
    () => getVisibleCreativeLayerEntries(layerEntries),
    [layerEntries],
  );
  const layerSortableIds = useMemo(
    () => visibleLayerEntries.map((entry) => entry.block.id),
    [visibleLayerEntries],
  );
  const selectedBlockExists = Boolean(
    selectedBlockId && editableBlocks.some((block) => block.id === selectedBlockId),
  );
  const selectedBlockIdsInScene = useMemo(
    () =>
      selectedBlockIds.filter((blockId) => editableBlocks.some((block) => block.id === blockId)),
    [editableBlocks, selectedBlockIds],
  );
  const selectedBlock = editableBlocks.find((block) => block.id === selectedBlockId) ?? null;
  const selectedTransformBlockId = selectedBlockExists ? selectedBlockId : null;
  const selectedTransformBlockIds = selectedBlockIdsInScene;
  const layerDragSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    if (!post) {
      return;
    }

    const action = getPostDraftHydrationAction({
      postId: post.id,
      hydratedPostId: hydratedPostIdRef.current,
      lastServerDraftKey: lastServerDraftKeyRef.current,
      nextServerDraftKey: baselineDraftKey,
      currentClientDraftKey: currentDraftKey,
    });

    if (action === "preserve_local") {
      lastServerDraftKeyRef.current = baselineDraftKey;
      setDraftWarning(
        intl.formatMessage({
          id: "campaignPost.draftSyncWarning",
          defaultMessage: "A newer saved draft exists on the server. Your local edits were kept.",
        }),
      );
      return;
    }

    if (action === "acknowledge") {
      hydratedPostIdRef.current = post.id;
      lastServerDraftKeyRef.current = baselineDraftKey;
      setDraftWarning(null);
      return;
    }

    if (action !== "hydrate") {
      return;
    }

    hydratedPostIdRef.current = post.id;
    lastServerDraftKeyRef.current = baselineDraftKey;
    setDraftWarning(null);
    setDraft(baselineDraft);
    setSelectedCarouselSlideId(carouselSlides[0]?.slide.id ?? null);
    didClearSelectedBlockRef.current = false;
    didApplyFocusedSelectionRef.current = false;
    setSelectedBlockId(baselineDraft?.plan.scene.blocks[0]?.id ?? null);
    setSelectedBlockIds(
      baselineDraft?.plan.scene.blocks[0]?.id ? [baselineDraft.plan.scene.blocks[0].id] : [],
    );
    setDraftHistory(EMPTY_POST_DRAFT_HISTORY);
  }, [baselineDraft, baselineDraftKey, carouselSlides, currentDraftKey, intl, post]);

  useEffect(() => {
    if (didApplyFocusedSelectionRef.current || !focusedEditorSelection) {
      return;
    }

    const targetSlide = getFocusedEditorSelectionSlide(focusedEditorSelection, carouselSlides);
    if (targetSlide && selectedCarouselSlideId !== targetSlide.slide.id) {
      setSelectedCarouselSlideId(targetSlide.slide.id);
      return;
    }

    if (
      focusedEditorSelection.blockId &&
      editableBlocks.some((block) => block.id === focusedEditorSelection.blockId)
    ) {
      didClearSelectedBlockRef.current = false;
      didApplyFocusedSelectionRef.current = true;
      setSelectedBlockId(focusedEditorSelection.blockId);
      setSelectedBlockIds([focusedEditorSelection.blockId]);
      setInspectorTab("layers");
      return;
    }

    if (!focusedEditorSelection.blockId && targetSlide) {
      didApplyFocusedSelectionRef.current = true;
    }
  }, [carouselSlides, editableBlocks, focusedEditorSelection, selectedCarouselSlideId]);

  useEffect(() => {
    if (selectedBlockId === null) {
      if (didClearSelectedBlockRef.current || editableBlocks.length === 0) {
        return;
      }

      const nextBlockId = editableBlocks[0]?.id ?? null;
      setSelectedBlockId(nextBlockId);
      setSelectedBlockIds(nextBlockId ? [nextBlockId] : []);
      return;
    }

    if (selectedBlockId && editableBlocks.some((block) => block.id === selectedBlockId)) {
      didClearSelectedBlockRef.current = false;
      if (selectedBlockIds.length !== selectedBlockIdsInScene.length) {
        setSelectedBlockIds(selectedBlockIdsInScene);
      }
      return;
    }

    didClearSelectedBlockRef.current = false;
    const nextBlockId = editableBlocks[0]?.id ?? null;
    setSelectedBlockId(nextBlockId);
    setSelectedBlockIds(nextBlockId ? [nextBlockId] : []);
  }, [editableBlocks, selectedBlockId, selectedBlockIds.length, selectedBlockIdsInScene]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.tagName === "SELECT" ||
        target?.tagName === "BUTTON" ||
        target?.isContentEditable
      ) {
        return;
      }

      const isUndoRedoShortcut = event.metaKey || event.ctrlKey;
      const key = event.key.toLowerCase();

      if (!isUndoRedoShortcut && !event.altKey && (key === "backspace" || key === "delete")) {
        if (
          !event.repeat &&
          selectedBlockIdsInScene.length > 0 &&
          activePlan &&
          activePlan.scene.blocks.length > 1
        ) {
          event.preventDefault();
          removeSelectedLayer();
        }
        return;
      }

      if (!isUndoRedoShortcut) {
        return;
      }

      if (key === "z" && event.shiftKey) {
        event.preventDefault();
        redoDraftChange();
        return;
      }

      if (key === "z") {
        event.preventDefault();
        undoDraftChange();
        return;
      }

      if (key === "y") {
        event.preventDefault();
        redoDraftChange();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  if (brandQuery.isPending) {
    return (
      <CenteredLoader
        label={intl.formatMessage({
          id: "standalonePost.loading",
          defaultMessage: "Loading post...",
        })}
      />
    );
  }

  if (!brand || brand.status !== "scanned") {
    return <Navigate to={getUnavailableBrandPath(brand?.id)} replace />;
  }

  if (batchQuery.isPending) {
    return (
      <CenteredLoader
        label={intl.formatMessage({
          id: "standalonePost.loading",
          defaultMessage: "Loading post...",
        })}
      />
    );
  }

  if (batchQuery.error) {
    return (
      <div className="mx-auto w-full max-w-5xl px-6 py-8">
        <EmptyCard>
          <FormattedMessage
            id="standalonePost.error.loadFailed"
            defaultMessage="Failed to load post: {message}"
            values={{ message: batchQuery.error.message }}
          />
        </EmptyCard>
      </div>
    );
  }

  if (!batch || !post || !draft || !activePlan) {
    return (
      <div className="mx-auto w-full max-w-5xl space-y-6 px-6 py-8">
        <Link
          to={buildBrandPath(brand.id, "/review")}
          className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          <FormattedMessage id="standalonePost.backToReview" defaultMessage="Back to review" />
        </Link>
        <EmptyCard>
          <FormattedMessage
            id="standalonePost.notReady"
            defaultMessage="This post is not ready to edit yet."
          />
        </EmptyCard>
      </div>
    );
  }

  const handleReturn = () => {
    if (isFocusedMode && hasBackgroundLocation) {
      navigate(-1);
      return;
    }

    navigate(buildBrandPath(brand.id, "/review"));
  };

  const downloadImage = async () => {
    setDownloadingImage(true);

    try {
      await downloadSocialPostPreviewImage({
        stage: downloadStageRef.current,
        previewAssetUrl: null,
        fileName: `standalone-${post.id}.png`,
      });
    } catch (caught) {
      toast.error(
        caught instanceof Error
          ? caught.message
          : intl.formatMessage({
              id: "approvals.downloadFailed",
              defaultMessage: "Could not download the image.",
            }),
      );
    } finally {
      setDownloadingImage(false);
    }
  };

  const saveCurrentChanges = async () => {
    if (!isPostEditable) {
      return;
    }

    const draftToSave = upsertFocusedGeneratedAssetVariantDraft({
      draft,
      selection: focusedEditorSelection,
    });
    const normalizedDraft = normalizeSocialPostDraftLogoAssetKeys({
      draft: draftToSave,
      logoAssets: logosQuery.data?.logos ?? [],
    });
    const parsed = socialPostEditableDraftSchema.parse(normalizedDraft);

    try {
      const saved = await saveDraftMutation.mutateAsync({
        brandId: brand.id,
        batchId: batch.id,
        scheduledPostId: post.id,
        draft: parsed,
      });
      await publishDraftMutation.mutateAsync({
        brandId: brand.id,
        batchId: batch.id,
        scheduledPostId: post.id,
        expectedDraftUpdatedAt: saved.updatedAt,
      });
    } catch {
      // Mutation errors are rendered in the editor action area.
    }
  };

  const publishNow = () => {
    if (!canPublishNow) {
      return;
    }

    publishNowMutation.mutate({
      brandId: brand.id,
      batchId: batch.id,
      scheduledPostId: post.id,
    });
  };

  const submitImageRegeneration = async (request: {
    blockId: string;
    sourceGeneratedAssetId: string;
    prompt?: string;
    brushRadius: number;
    strokes: Array<{ id: string; points: Array<{ x: number; y: number }> }>;
  }) => {
    if (!isPostEditable) {
      return false;
    }

    if (imageRegenerationResolveRef.current) {
      return false;
    }

    const regenerationRequest: PartialImageRegenerationRequest = {
      type: "social_post_image_regeneration.request",
      version: 1,
      mode: "partial",
      standaloneAssetBatchId: batch.id,
      scheduledPostId: post.id,
      applyAsDraftOverride: true,
      ...request,
    };

    setPendingImageRegenerationRequest(regenerationRequest);

    return await new Promise<boolean>((resolve) => {
      imageRegenerationResolveRef.current = resolve;
    });
  };

  const confirmImageRegeneration = async () => {
    if (!pendingImageRegenerationRequest) {
      return;
    }

    try {
      await regenerateImageMutation.mutateAsync({
        brandId: brand.id,
        batchId: batch.id,
        request: pendingImageRegenerationRequest,
      });
      imageRegenerationResolveRef.current?.(true);
      imageRegenerationResolveRef.current = null;
      setPendingImageRegenerationRequest(null);
    } catch {
      // Keep the dialog open so the API error can be shown and retried.
    }
  };

  const cancelImageRegeneration = () => {
    if (regenerateImageMutation.isPending) {
      return;
    }

    imageRegenerationResolveRef.current?.(false);
    imageRegenerationResolveRef.current = null;
    setPendingImageRegenerationRequest(null);
  };

  const updateDraft = (nextDraft: SocialPostEditableDraft, trackHistory = true) => {
    if (!isPostEditable) {
      return;
    }

    setDraftWarning(null);
    if (trackHistory && draft) {
      setDraftHistory((history) =>
        pushPostDraftHistory({ history, current: draft, next: nextDraft }),
      );
    }
    setDraft(nextDraft);
  };

  const updateActivePlan = (
    nextPlan: SocialPostScenePlanV1,
    trackHistory = true,
    targetCarouselSlide: (typeof carouselSlides)[number] | null = activeCarouselSlide,
  ) => {
    if (!draft) {
      return;
    }

    if (!targetCarouselSlide) {
      updateDraft({ ...draft, plan: nextPlan }, trackHistory);
      return;
    }

    const nextCarouselSlides = draft.carouselSlides.map((slideDraft) =>
      slideDraft.slideId === targetCarouselSlide.slide.id
        ? { ...slideDraft, plan: nextPlan }
        : slideDraft,
    );
    const hasActiveSlideDraft = nextCarouselSlides.some(
      (slideDraft) => slideDraft.slideId === targetCarouselSlide.slide.id,
    );
    if (!hasActiveSlideDraft) {
      nextCarouselSlides.push({
        slideId: targetCarouselSlide.slide.id,
        slideIndex: targetCarouselSlide.slide.index,
        plan: nextPlan,
        imageAssetOverrides: [],
      });
    }

    updateDraft(
      {
        ...draft,
        plan:
          targetCarouselSlide.slide.index === 1 || draft.carouselSlides.length === 0
            ? nextPlan
            : (nextCarouselSlides.find((slideDraft) => slideDraft.slideIndex === 1)?.plan ??
              draft.plan),
        carouselSlides: nextCarouselSlides.toSorted((a, b) => a.slideIndex - b.slideIndex),
      },
      trackHistory,
    );
  };

  const applyOperations = (summary: string, operations: SocialPostTemplateEditOperation[]) => {
    if (!isPostEditable) {
      return;
    }

    if (!draft || !activePlan) {
      return;
    }

    const nextActiveDraft = applySocialPostTemplateEditBatch(
      {
        ...draft,
        plan: activePlan,
        imageAssetOverrides: activeCarouselSlideDraft?.imageAssetOverrides ?? [],
      },
      { summary, operations },
    );
    updateDraft(
      mergeActiveSocialPostEditDraft({
        baseDraft: draft,
        activeDraft: nextActiveDraft,
        carouselTarget: activeCarouselSlide
          ? {
              slideId: activeCarouselSlide.slide.id,
              slideIndex: activeCarouselSlide.slide.index,
            }
          : null,
      }),
    );
  };

  const updateBlockById = (
    blockId: string,
    changes: Record<string, unknown>,
    targetCarouselSlide: (typeof carouselSlides)[number] | null = activeCarouselSlide,
  ) => {
    if (!isPostEditable) {
      return;
    }

    const targetPlan = targetCarouselSlide
      ? (draft?.carouselSlides.find(
          (slideDraft) => slideDraft.slideId === targetCarouselSlide.slide.id,
        )?.plan ??
        targetCarouselSlide.scenePlan?.plan ??
        null)
      : activePlan;

    if (!draft || !targetPlan) {
      return;
    }

    updateActivePlan(updateBlockInPlan(targetPlan, blockId, changes), true, targetCarouselSlide);
  };

  const updateBlocksById = (
    updates: Array<{ blockId: string; changes: Record<string, unknown> }>,
    targetCarouselSlide: (typeof carouselSlides)[number] | null = activeCarouselSlide,
  ) => {
    if (!isPostEditable) {
      return;
    }

    const targetPlan = targetCarouselSlide
      ? (draft?.carouselSlides.find(
          (slideDraft) => slideDraft.slideId === targetCarouselSlide.slide.id,
        )?.plan ??
        targetCarouselSlide.scenePlan?.plan ??
        null)
      : activePlan;

    if (!draft || !targetPlan || updates.length === 0) {
      return;
    }

    updateActivePlan(
      updates.reduce(
        (plan, update) => updateBlockInPlan(plan, update.blockId, update.changes),
        targetPlan,
      ),
      true,
      targetCarouselSlide,
    );
  };

  const uploadLogoForSelectedBlock = async (file: File) => {
    if (!brand.id || selectedBlock?.type !== "logo") {
      return;
    }

    const upload = await createLogoUpload.mutateAsync({
      brandId: brand.id,
      fileName: file.name,
      contentType: file.type as "image/png" | "image/jpeg" | "image/webp" | "image/svg+xml",
      sizeBytes: file.size,
    });
    const response = await fetch(upload.uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": upload.contentType },
      body: file,
    });

    if (!response.ok) {
      throw new Error(`Logo upload failed with status ${response.status}`);
    }

    const result = await completeLogoUpload.mutateAsync({
      brandId: brand.id,
      objectKey: upload.objectKey,
      fileName: file.name,
      contentType: upload.contentType,
      sizeBytes: file.size,
      makePrimary: false,
      regenerateVisualIdentity: false,
    });
    await utils.brand.listLogos.invalidate({ brandId: brand.id });
    updateBlockById(selectedBlock.id, { assetObjectKey: result.logo.objectKey });
  };

  const deleteLogoFromPicker = async (logo: CreativeLogoOption) => {
    if (!brand.id || logo.isPrimary || logo.id.startsWith("preview:")) {
      return;
    }

    await deleteLogo.mutateAsync({ brandId: brand.id, logoId: logo.id });
    await utils.brand.listLogos.invalidate({ brandId: brand.id });

    const primaryLogo = (logosQuery.data?.logos ?? []).find((candidate) => candidate.isPrimary);
    if (
      selectedBlock?.type === "logo" &&
      selectedBlock.assetObjectKey === logo.objectKey &&
      primaryLogo
    ) {
      updateBlockById(selectedBlock.id, { assetObjectKey: primaryLogo.objectKey });
    }
  };

  const addLayer = (kind: CreativeLayerCreateKind) => {
    if (!isPostEditable || !draft || !activePlan) {
      return;
    }
    if (kind === "logo" && !canAddLogoLayer) {
      return;
    }

    const defaultLogoObjectKey =
      logoOptions.find((logo) => logo.isPrimary)?.objectKey ?? logoOptions[0]?.objectKey ?? null;

    const block = createDefaultCreativeLayerBlock({
      kind,
      id: createLayerId(kind),
      canvas: activePlan.scene.canvas,
      zIndex: getNextRootLayerZIndex(activePlan.scene.blocks),
      fontFamily: activePlan.scene.fonts[0]?.family,
      logoObjectKey: defaultLogoObjectKey,
    });

    applyOperations(`Add ${kind} layer`, [{ type: "add_block", block }]);
    didClearSelectedBlockRef.current = false;
    setSelectedBlockId(block.id);
    setSelectedBlockIds([block.id]);
  };

  function removeSelectedLayer() {
    if (!draft || !activePlan || !isPostEditable || selectedBlockIdsInScene.length === 0) {
      return;
    }

    const removableBlockIds = selectedBlockIdsInScene.slice(
      0,
      Math.max(0, activePlan.scene.blocks.length - 1),
    );
    if (removableBlockIds.length === 0) {
      return;
    }

    const removableBlockIdSet = new Set(removableBlockIds);
    const remainingBlocks = activePlan.scene.blocks.filter(
      (block) => !removableBlockIdSet.has(block.id),
    );
    const compactionOperations = createCreativeLayerCompactionUpdates(remainingBlocks).flatMap(
      (update) => {
        const block = remainingBlocks.find((candidate) => candidate.id === update.blockId);
        const operation = block ? createBlockEditOperation(block, update.changes) : null;
        return operation ? [operation] : [];
      },
    );

    applyOperations("Remove selected layer", [
      ...removableBlockIds.map((blockId) => ({ type: "remove_block" as const, blockId })),
      ...compactionOperations,
    ]);
    didClearSelectedBlockRef.current = true;
    setSelectedBlockId(null);
    setSelectedBlockIds([]);
  }

  const reorderLayer = (event: DragEndEvent) => {
    const overId = event.over?.id;
    const activeId = event.active.id;

    if (!draft || !activePlan || !overId || activeId === overId) {
      return;
    }

    const updates = createCreativeLayerReorderUpdates({
      blocks: activePlan.scene.blocks,
      activeBlockId: String(activeId),
      overBlockId: String(overId),
    });

    updateBlocksById(updates);
  };

  const moveLayerForward = () => {
    if (!activePlan || !selectedBlockId) return;
    updateBlocksById(
      createCreativeLayerMoveStepUpdates({
        blocks: activePlan.scene.blocks,
        blockId: selectedBlockId,
        direction: "forward",
      }),
    );
  };

  const moveLayerBackward = () => {
    if (!activePlan || !selectedBlockId) return;
    updateBlocksById(
      createCreativeLayerMoveStepUpdates({
        blocks: activePlan.scene.blocks,
        blockId: selectedBlockId,
        direction: "backward",
      }),
    );
  };

  const moveLayerToFront = () => {
    if (!activePlan || !selectedBlockId) return;
    updateBlocksById(
      createCreativeLayerMoveEdgeUpdates({
        blocks: activePlan.scene.blocks,
        blockId: selectedBlockId,
        edge: "front",
      }),
    );
  };

  const moveLayerToBack = () => {
    if (!activePlan || !selectedBlockId) return;
    updateBlocksById(
      createCreativeLayerMoveEdgeUpdates({
        blocks: activePlan.scene.blocks,
        blockId: selectedBlockId,
        edge: "back",
      }),
    );
  };

  const selectLayer = (blockId: string, slideId?: string, options?: { toggle?: boolean }) => {
    if (slideId) {
      setSelectedCarouselSlideId(slideId);
    }

    if (!blockId) {
      didClearSelectedBlockRef.current = true;
      setSelectedBlockId(null);
      setSelectedBlockIds([]);
      setInspectorTab("layers");
      return;
    }

    didClearSelectedBlockRef.current = false;
    if (options?.toggle) {
      setSelectedBlockIds((current) => {
        const next = current.includes(blockId)
          ? current.filter((candidate) => candidate !== blockId)
          : [...current, blockId];
        setSelectedBlockId(next.at(-1) ?? null);
        didClearSelectedBlockRef.current = next.length === 0;
        return next;
      });
      setInspectorTab("layers");
      return;
    }

    setSelectedBlockId(blockId);
    setSelectedBlockIds([blockId]);
    setInspectorTab("layers");
  };

  const selectLayers = (
    blockIds: string[],
    slideId?: string,
    validBlocks: SocialPostSceneBlockV1[] = editableBlocks,
  ) => {
    if (slideId) {
      setSelectedCarouselSlideId(slideId);
    }

    const validBlockIds = blockIds.filter((blockId) =>
      validBlocks.some((block) => block.id === blockId),
    );

    didClearSelectedBlockRef.current = validBlockIds.length === 0;
    setSelectedBlockId(validBlockIds.at(-1) ?? null);
    setSelectedBlockIds(validBlockIds);
    setInspectorTab("layers");
  };

  const undoDraftChange = () => {
    if (!isPostEditable) {
      return;
    }

    const result = undoPostDraftHistory({ history: draftHistory, current: draft });
    setDraftHistory(result.history);
    setDraft(result.draft ?? baselineDraft);
  };

  const redoDraftChange = () => {
    if (!isPostEditable) {
      return;
    }

    const result = redoPostDraftHistory({ history: draftHistory, current: draft });
    setDraftHistory(result.history);
    if (result.draft) {
      setDraft(result.draft);
    }
  };

  return (
    <>
      <CreativeEditorStudio
        className={isFocusedMode ? "fixed inset-0 z-20" : "h-full w-full"}
        eyebrow={
          <FormattedMessage id="standalonePost.editorEyebrow" defaultMessage="On-demand creative" />
        }
        title={post.slot.concept}
        status={
          saveDraftMutation.isPending || publishDraftMutation.isPending ? (
            <FormattedMessage id="common.saving" defaultMessage="Saving…" />
          ) : hasUnsavedChanges ? (
            <FormattedMessage
              id="standalonePost.status.unsavedChanges"
              defaultMessage="Unsaved changes"
            />
          ) : null
        }
        backAction={
          isFocusedMode ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="-ml-3 w-fit gap-2 rounded-full text-foreground/62 hover:bg-foreground/6 hover:text-foreground"
              onClick={handleReturn}
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              <FormattedMessage
                id={hasBackgroundLocation ? "common.back" : "standalonePost.backToReview"}
                defaultMessage={hasBackgroundLocation ? "Back" : "Back to review"}
              />
            </Button>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              asChild
              className="-ml-3 w-fit gap-2 rounded-full text-foreground/62 hover:bg-foreground/6 hover:text-foreground"
            >
              <Link to={buildBrandPath(brand.id, "/review")}>
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                <FormattedMessage
                  id="standalonePost.backToReview"
                  defaultMessage="Back to review"
                />
              </Link>
            </Button>
          )
        }
        actions={
          <>
            <PostPreviewOverflowMenu
              downloading={downloadingImage}
              onDownload={() => void downloadImage()}
            />
            <Button
              type="button"
              size="sm"
              className="rounded-full bg-foreground text-primary-foreground shadow-[0_10px_30px_rgba(19,34,49,0.16)] hover:bg-foreground/90"
              disabled={!draftState.canSaveChanges}
              onClick={() => void saveCurrentChanges()}
            >
              {saveDraftMutation.isPending || publishDraftMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
              ) : (
                <Save className="h-3.5 w-3.5" aria-hidden="true" />
              )}
              <FormattedMessage id="common.save" defaultMessage="Save" />
            </Button>
            {shouldShowPublishNow ? (
              <Button
                type="button"
                size="sm"
                variant="brand"
                className="rounded-full"
                disabled={!canPublishNow}
                onClick={publishNow}
              >
                {publishNowMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                ) : (
                  <UploadCloud className="h-3.5 w-3.5" aria-hidden="true" />
                )}
                <FormattedMessage
                  id="standalonePost.actions.publishNow"
                  defaultMessage="Publish now"
                />
              </Button>
            ) : null}
          </>
        }
        warnings={
          !isPostEditable ? (
            <FormattedMessage
              id="standalonePost.editLockedApproved"
              defaultMessage="This post is approved and can no longer be edited. Create a new draft or unapprove it before making changes."
            />
          ) : (
            draftWarning
          )
        }
        errors={
          saveDraftMutation.error || publishDraftMutation.error || publishNowMutation.error
            ? (saveDraftMutation.error?.message ??
              publishDraftMutation.error?.message ??
              publishNowMutation.error?.message)
            : null
        }
        canvas={
          <>
            <div className="pointer-events-none fixed -left-[10000px] top-0 opacity-0" aria-hidden>
              <SocialPostSceneRenderer
                plan={activePlan}
                linkedAssets={activeLinkedAssets}
                brandkit={brandkit}
                logoAssets={logoAssets}
                stageRef={downloadStageRef}
              />
            </div>
            {isCarouselPost ? (
              <div className="relative min-h-0 flex-1 overflow-hidden rounded-[2rem] border border-border/70 bg-[#fffdf8] shadow-[0_26px_70px_rgba(19,34,49,0.14)]">
                <div
                  ref={setCarouselToolbarHost}
                  className="pointer-events-none absolute inset-0 z-50"
                />
                <div
                  className="pointer-events-none absolute inset-0 opacity-95"
                  style={{
                    backgroundImage:
                      "radial-gradient(circle at 50% 14%, rgba(255,241,212,0.38), transparent 24rem), radial-gradient(circle at 18% 84%, rgba(255,241,212,0.42), transparent 18rem), linear-gradient(rgba(19,34,49,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(19,34,49,0.04) 1px, transparent 1px), radial-gradient(circle at 1px 1px, rgba(19,34,49,0.09) 1px, transparent 0)",
                    backgroundPosition: "-1px -1px, -1px -1px, 0 0",
                    backgroundSize: "auto, auto, 96px 96px, 96px 96px, 12px 12px",
                  }}
                  aria-hidden="true"
                />
                <div className="relative h-full min-h-[34rem] overflow-auto px-6 py-8">
                  <div className="flex min-w-max items-start gap-12 pr-6">
                    {carouselSlides.map((slideOutput) => {
                      const slideDraft = draft.carouselSlides.find(
                        (candidate) => candidate.slideId === slideOutput.slide.id,
                      );
                      const slidePlan = slideDraft?.plan ?? slideOutput.scenePlan?.plan ?? null;
                      const isActiveSlide = activeCarouselSlide?.slide.id === slideOutput.slide.id;
                      const slideLinkedAssets = applyFocusedLinkedAssetSelection({
                        linkedAssets: slideOutput.assets,
                        selection:
                          focusedEditorSelection &&
                          (focusedEditorSelection.slideId === slideOutput.slide.id ||
                            focusedEditorSelection.slideIndex === slideOutput.slide.index)
                            ? focusedEditorSelection
                            : null,
                      });

                      if (!slidePlan) {
                        return null;
                      }

                      return (
                        <div
                          key={slideOutput.slide.id}
                          className={[
                            "relative flex shrink-0 flex-col gap-3",
                            isActiveSlide ? "z-20" : "z-0",
                          ].join(" ")}
                          style={{
                            width: `min(${Math.round(480 * carouselWorkspaceZoom)}px, calc(100vw - 4rem))`,
                          }}
                        >
                          <button
                            type="button"
                            className={[
                              "flex items-center justify-between rounded-2xl border px-3 py-2 text-left text-xs transition-colors",
                              isActiveSlide
                                ? "border-brand/55 bg-brand/12 text-foreground shadow-[0_10px_30px_rgba(19,34,49,0.08)]"
                                : "border-border/60 bg-background/62 text-foreground/55 hover:border-foreground/20 hover:bg-background/78 hover:text-foreground",
                            ].join(" ")}
                            onClick={() => {
                              setSelectedCarouselSlideId(slideOutput.slide.id);
                              const nextBlockId = getCarouselArtboardSelectionBlockId({
                                currentBlockId: selectedBlockId,
                                slideBlocks: slidePlan.scene.blocks,
                              });
                              if (nextBlockId !== selectedBlockId) {
                                setSelectedBlockId(nextBlockId);
                                setSelectedBlockIds(nextBlockId ? [nextBlockId] : []);
                              }
                            }}
                          >
                            <span className="font-semibold uppercase tracking-[0.16em]">
                              <FormattedMessage
                                id="standalonePost.carouselSlideLabel"
                                defaultMessage="Slide {index}"
                                values={{ index: slideOutput.slide.index }}
                              />
                            </span>
                            <span className="truncate pl-3 font-medium capitalize">
                              {slideOutput.slide.role}
                            </span>
                          </button>
                          <div
                            className={[
                              "rounded-[1.6rem] border bg-background/74 p-2 transition-[border-color,box-shadow,background-color]",
                              isActiveSlide
                                ? "border-brand/55 shadow-[0_18px_54px_rgba(19,34,49,0.16)]"
                                : "border-border/65 shadow-[0_14px_42px_rgba(19,34,49,0.08)]",
                            ].join(" ")}
                          >
                            <CampaignCreativeScenePreview
                              plan={slidePlan}
                              linkedAssets={slideLinkedAssets}
                              brandkit={brandkit}
                              logoAssets={logoAssets}
                              profileLabel={brand.sourceDomain}
                              caption={draft.caption ?? ""}
                              hashtags={draft.hashtags}
                              maxWidth={448 * carouselWorkspaceZoom}
                              selectedBlockId={isActiveSlide ? selectedBlockId : null}
                              selectedBlockIds={isActiveSlide ? selectedBlockIdsInScene : []}
                              selectedTransformBlockId={
                                isActiveSlide ? selectedTransformBlockId : null
                              }
                              selectedTransformBlockIds={
                                isActiveSlide ? selectedTransformBlockIds : []
                              }
                              showWorkspaceControls={isActiveSlide}
                              workspaceControlsPortal={carouselToolbarHost}
                              workspaceZoomLabel={`${Math.round(carouselWorkspaceZoom * 100)}%`}
                              onWorkspaceZoomOut={() =>
                                setCarouselWorkspaceZoom((current) => Math.max(0.6, current * 0.85))
                              }
                              onWorkspaceFitView={() => setCarouselWorkspaceZoom(1)}
                              onWorkspaceZoomIn={() =>
                                setCarouselWorkspaceZoom((current) => Math.min(2, current * 1.15))
                              }
                              onWorkspaceWheelZoom={(factor) =>
                                setCarouselWorkspaceZoom((current) =>
                                  Math.min(2, Math.max(0.6, current * factor)),
                                )
                              }
                              flat={true}
                              artboard={true}
                              onCaptionDoubleClick={() => {
                                setSelectedCarouselSlideId(slideOutput.slide.id);
                                setInspectorTab("caption");
                              }}
                              onSelectBlock={(blockId, options) =>
                                selectLayer(blockId, slideOutput.slide.id, options)
                              }
                              onSelectBlocks={(blockIds) =>
                                selectLayers(blockIds, slideOutput.slide.id, slidePlan.scene.blocks)
                              }
                              onUpdateBlock={(blockId, changes) => {
                                setSelectedCarouselSlideId(slideOutput.slide.id);
                                updateBlockById(blockId, changes, slideOutput);
                              }}
                              onUpdateBlocks={(updates) => {
                                setSelectedCarouselSlideId(slideOutput.slide.id);
                                updateBlocksById(updates, slideOutput);
                              }}
                              isImageRegenerationPending={regenerateImageMutation.isPending}
                              activeImageRegenerationBlockIds={post.activeImageRegenerationBlockIds}
                              imageRegenerationError={
                                regenerateImageMutation.error?.message ?? null
                              }
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <CampaignCreativeScenePreview
                plan={activePlan}
                linkedAssets={activeLinkedAssets}
                brandkit={brandkit}
                logoAssets={logoAssets}
                profileLabel={brand.sourceDomain}
                caption={draft.caption ?? ""}
                hashtags={draft.hashtags}
                onCaptionDoubleClick={() => setInspectorTab("caption")}
                selectedBlockId={selectedBlockId}
                selectedBlockIds={selectedBlockIdsInScene}
                selectedTransformBlockId={selectedTransformBlockId}
                selectedTransformBlockIds={selectedTransformBlockIds}
                canUndo={draftHistory.past.length > 0}
                canRedo={draftHistory.future.length > 0}
                onUndo={undoDraftChange}
                onRedo={redoDraftChange}
                onSelectBlock={(blockId, options) => selectLayer(blockId, undefined, options)}
                onSelectBlocks={(blockIds) => selectLayers(blockIds)}
                onUpdateBlock={updateBlockById}
                onUpdateBlocks={updateBlocksById}
                onSubmitImageRegeneration={submitImageRegeneration}
                isImageRegenerationPending={regenerateImageMutation.isPending}
                activeImageRegenerationBlockIds={post.activeImageRegenerationBlockIds}
                imageRegenerationError={regenerateImageMutation.error?.message ?? null}
              />
            )}
          </>
        }
        inspector={
          showLayersDrawer ? (
            <>
              <div className="mb-4 rounded-3xl border border-border/70 bg-background/80 p-3 shadow-[0_12px_30px_rgba(19,34,49,0.06)]">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <BringToFront className="h-4 w-4 text-foreground/65" aria-hidden="true" />
                    <span className="font-display text-base font-semibold tracking-[-0.02em] text-foreground">
                      <FormattedMessage id="standalonePost.layers.title" defaultMessage="Layers" />
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <AddLayerMenu
                      compact
                      align="right"
                      disabled={!isPostEditable || !activePlan}
                      label={intl.formatMessage({
                        id: "standalonePost.addLayer",
                        defaultMessage: "Add layer",
                      })}
                      items={[
                        { label: "Text", icon: Type, onClick: () => addLayer("text") },
                        ...(canAddLogoLayer
                          ? [{ label: "Logo", icon: ImageIcon, onClick: () => addLayer("logo") }]
                          : []),
                        {
                          label: "Shapes",
                          icon: Shapes,
                          children: [
                            { label: "Rectangle", icon: Square, onClick: () => addLayer("rect") },
                            {
                              label: "Ellipse / line",
                              icon: Shapes,
                              onClick: () => addLayer("shape"),
                            },
                          ],
                        },
                        { label: "Frame", icon: Frame, onClick: () => addLayer("frame") },
                      ]}
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      className="h-8 w-8 rounded-full p-0"
                      onClick={() => setShowLayersDrawer(false)}
                      aria-label={intl.formatMessage({
                        id: "standalonePost.layersDrawerToggle",
                        defaultMessage: "Layers",
                      })}
                    >
                      <BringToFront className="h-3.5 w-3.5" aria-hidden="true" />
                    </Button>
                  </div>
                </div>
                <p className="mt-2 text-xs leading-5 text-foreground/48">
                  <FormattedMessage
                    id="standalonePost.layers.description"
                    defaultMessage="Reorder, hide, or select layers in this post."
                  />
                </p>
              </div>
              <CreativeLayersPanel
                title={
                  <FormattedMessage id="standalonePost.layers.title" defaultMessage="Layers" />
                }
                count={editableBlocks.length}
                actions={
                  <div className="flex flex-wrap items-center justify-end gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="justify-center rounded-2xl border-destructive/20 bg-destructive/6 text-destructive hover:bg-destructive/10 disabled:border-border/60 disabled:bg-muted/35 disabled:text-foreground/32"
                      disabled={
                        selectedBlockIdsInScene.length === 0 || activePlan.scene.blocks.length <= 1
                      }
                      onClick={removeSelectedLayer}
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                      <FormattedMessage
                        id="standalonePost.layers.deleteLayer"
                        defaultMessage="Delete layer"
                      />
                    </Button>
                  </div>
                }
              >
                <DndContext
                  sensors={layerDragSensors}
                  collisionDetection={closestCenter}
                  autoScroll={false}
                  onDragEnd={reorderLayer}
                >
                  <SortableContext items={layerSortableIds} strategy={verticalListSortingStrategy}>
                    <div className="grid gap-1.5">
                      {visibleLayerEntries.map((entry) => (
                        <SortableLayerRow
                          key={entry.block.id}
                          entry={entry}
                          isActive={selectedBlockIdsInScene.includes(entry.block.id)}
                          isTransformActive={false}
                          onSelectBlock={(blockId, options) =>
                            selectLayer(blockId, undefined, options)
                          }
                          onRenameBlock={(blockId, layerName) =>
                            updateBlockById(blockId, { layerName })
                          }
                          onToggleVisibility={(blockId, visible) =>
                            updateBlockById(blockId, { visible })
                          }
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </CreativeLayersPanel>
            </>
          ) : (
            <>
              <StandaloneLayerInspector
                selectedBlock={selectedBlock}
                availableFontFamilies={availableFontFamilies}
                logoOptions={logoOptions}
                brandColors={brandPrimaryColors}
                isLogoUploading={
                  createLogoUpload.isPending || completeLogoUpload.isPending || deleteLogo.isPending
                }
                onUploadLogo={uploadLogoForSelectedBlock}
                onDeleteLogo={deleteLogoFromPicker}
                onUpdateBlock={updateBlockById}
                showLayersDrawer={showLayersDrawer}
                onToggleLayersDrawer={() => setShowLayersDrawer((current) => !current)}
                tab={inspectorTab}
                caption={draft.caption ?? ""}
                hashtags={draft.hashtags}
                onCaptionChange={(caption) => updateDraft({ ...draft, caption })}
                onHashtagsChange={(hashtags) => updateDraft({ ...draft, hashtags })}
                onAddLayer={addLayer}
                canAddLogoLayer={canAddLogoLayer}
                onMoveLayerForward={moveLayerForward}
                onMoveLayerBackward={moveLayerBackward}
                onMoveLayerToFront={moveLayerToFront}
                onMoveLayerToBack={moveLayerToBack}
                onRemoveSelectedLayer={removeSelectedLayer}
                canAddLayers={isPostEditable && Boolean(activePlan)}
                canRemoveLayer={Boolean(
                  selectedBlockId &&
                  canRemoveSelectedLayer(activePlan.scene.blocks, selectedBlockId),
                )}
              />
            </>
          )
        }
      />
      <RunConfirmationDialog
        open={Boolean(pendingImageRegenerationRequest)}
        title={intl.formatMessage({
          id: "standalonePost.imageRunConfirmTitle",
          defaultMessage: "Use runs to regenerate this image?",
        })}
        description={intl.formatMessage({
          id: "standalonePost.imageRunConfirmDescription",
          defaultMessage: "Cheetah will queue a new image generation for this standalone post.",
        })}
        confirmLabel={intl.formatMessage({
          id: "standalonePost.imageRunConfirmAction",
          defaultMessage: "Regenerate and use runs",
        })}
        quote={imageRegenerationQuoteQuery.data ?? null}
        quotePending={
          imageRegenerationQuoteQuery.isPending || imageRegenerationQuoteQuery.isFetching
        }
        confirmPending={regenerateImageMutation.isPending}
        error={
          imageRegenerationQuoteQuery.error?.message ??
          regenerateImageMutation.error?.message ??
          null
        }
        onCancel={cancelImageRegeneration}
        upgradeLabel={assetCreationGate.upgradeLabel}
        onUpgrade={assetCreationGate.openUpgradeModal}
        onConfirm={() => void confirmImageRegeneration()}
      />
      {assetCreationGate.upgradeModal}
    </>
  );
}

export default StandalonePostPage;

function hasBackgroundLocationState(state: unknown): boolean {
  return Boolean(state && typeof state === "object" && "backgroundLocation" in state);
}

function getFocusedEditorSelectionState(state: unknown): FocusedEditorSelectionState | null {
  if (!state || typeof state !== "object" || !("focusedEditorSelection" in state)) {
    return null;
  }

  const selection = (state as { focusedEditorSelection?: unknown }).focusedEditorSelection;
  if (!selection || typeof selection !== "object") {
    return null;
  }

  const candidate = selection as {
    blockId?: unknown;
    generatedAssetId?: unknown;
    slideId?: unknown;
    slideIndex?: unknown;
    asset?: unknown;
  };

  return {
    blockId: typeof candidate.blockId === "string" ? candidate.blockId : null,
    generatedAssetId:
      typeof candidate.generatedAssetId === "string" ? candidate.generatedAssetId : null,
    slideId: typeof candidate.slideId === "string" ? candidate.slideId : null,
    slideIndex: typeof candidate.slideIndex === "number" ? candidate.slideIndex : null,
    asset: getFocusedEditorSelectionAsset(candidate.asset),
  };
}

function getFocusedEditorSelectionAsset(input: unknown): CampaignCreativeLinkedAsset | null {
  if (!input || typeof input !== "object") {
    return null;
  }

  const candidate = input as {
    generatedAssetId?: unknown;
    blockId?: unknown;
    signedUrl?: unknown;
    width?: unknown;
    height?: unknown;
  };

  if (
    typeof candidate.generatedAssetId !== "string" ||
    typeof candidate.blockId !== "string" ||
    typeof candidate.signedUrl !== "string"
  ) {
    return null;
  }

  return {
    generatedAssetId: candidate.generatedAssetId,
    blockId: candidate.blockId,
    signedUrl: candidate.signedUrl,
    width: typeof candidate.width === "number" ? candidate.width : null,
    height: typeof candidate.height === "number" ? candidate.height : null,
  };
}

function applyFocusedLinkedAssetSelection(input: {
  linkedAssets: readonly CampaignCreativeLinkedAsset[];
  selection: FocusedEditorSelectionState | null;
}): CampaignCreativeLinkedAsset[] {
  const focusedAsset = input.selection?.asset;
  if (!focusedAsset || !input.selection?.blockId) {
    return [...input.linkedAssets];
  }

  return [
    ...input.linkedAssets.filter((asset) => asset.blockId !== input.selection?.blockId),
    focusedAsset,
  ];
}

function shouldUsePersistedDraftForFocusedSelection(input: {
  draft: SocialPostEditableDraft | null;
  selection: FocusedEditorSelectionState | null;
}) {
  if (!input.selection?.generatedAssetId || !input.selection.blockId || !input.draft) {
    return true;
  }

  return (
    getFocusedSelectionDraftGeneratedAssetId({
      draft: input.draft,
      selection: input.selection,
    }) === input.selection.generatedAssetId
  );
}

function applyFocusedGeneratedAssetSelectionToDraft(input: {
  draft: SocialPostEditableDraft | null;
  persistedDraft: SocialPostEditableDraft | null;
  selection: FocusedEditorSelectionState | null;
}): SocialPostEditableDraft | null {
  if (!input.draft || !input.selection?.blockId || !input.selection.generatedAssetId) {
    return input.draft;
  }

  const draftWithPersistedVariantDrafts = {
    ...input.draft,
    variantDrafts: input.persistedDraft?.variantDrafts ?? input.draft.variantDrafts ?? [],
  };
  const selectedVariantDraft = applySocialPostVariantDraft(
    draftWithPersistedVariantDrafts,
    input.selection.generatedAssetId,
  );

  const override = {
    blockId: input.selection.blockId,
    generatedAssetId: input.selection.generatedAssetId,
  };
  const updateOverrides = (overrides: SocialPostEditableDraft["imageAssetOverrides"]) => [
    ...overrides.filter((item) => item.blockId !== override.blockId),
    override,
  ];
  const isCarouselSelection =
    input.selection.slideId !== null || input.selection.slideIndex !== null;

  if (!isCarouselSelection) {
    return {
      ...selectedVariantDraft,
      imageAssetOverrides: updateOverrides(selectedVariantDraft.imageAssetOverrides),
    };
  }

  return {
    ...selectedVariantDraft,
    carouselSlides: selectedVariantDraft.carouselSlides.map((slide) =>
      slide.slideId === input.selection?.slideId || slide.slideIndex === input.selection?.slideIndex
        ? {
            ...slide,
            imageAssetOverrides: updateOverrides(slide.imageAssetOverrides),
          }
        : slide,
    ),
  };
}

function upsertFocusedGeneratedAssetVariantDraft(input: {
  draft: SocialPostEditableDraft | null;
  selection: FocusedEditorSelectionState | null;
}): SocialPostEditableDraft | null {
  if (!input.draft || !input.selection?.generatedAssetId) {
    return input.draft;
  }

  return upsertSocialPostVariantDraft(input.draft, input.selection.generatedAssetId);
}

function getFocusedSelectionDraftGeneratedAssetId(input: {
  draft: SocialPostEditableDraft;
  selection: FocusedEditorSelectionState;
}) {
  const overrides =
    input.selection.slideId !== null || input.selection.slideIndex !== null
      ? (input.draft.carouselSlides.find(
          (slide) =>
            slide.slideId === input.selection.slideId ||
            slide.slideIndex === input.selection.slideIndex,
        )?.imageAssetOverrides ?? [])
      : input.draft.imageAssetOverrides;

  return (
    overrides.find((override) => override.blockId === input.selection.blockId)?.generatedAssetId ??
    null
  );
}

function getFocusedEditorSelectionSlide<TSlide extends { slide: { id: string; index: number } }>(
  selection: FocusedEditorSelectionState,
  slides: readonly TSlide[],
): TSlide | null {
  if (selection.slideId) {
    return slides.find((slide) => slide.slide.id === selection.slideId) ?? null;
  }

  if (selection.slideIndex !== null) {
    return slides.find((slide) => slide.slide.index === selection.slideIndex) ?? null;
  }

  return null;
}

function SortableLayerRow({
  entry,
  isActive,
  isTransformActive,
  onSelectBlock,
  onRenameBlock,
  onToggleVisibility,
}: {
  entry: CreativeLayerEntry;
  isActive: boolean;
  isTransformActive: boolean;
  onSelectBlock: (blockId: string, options?: { toggle?: boolean }) => void;
  onRenameBlock: (blockId: string, layerName: string) => void;
  onToggleVisibility: (blockId: string, visible: boolean) => void;
}) {
  const intl = useIntl();
  const block = entry.block;
  const layerLabel = getLayerLabel(block, intl);

  return (
    <CreativeLayerSortableRow
      entry={entry}
      isActive={isActive}
      isTransformActive={isTransformActive}
      layerLabel={layerLabel}
      editableLayerName={getEditableLayerName(block, intl)}
      renameAriaLabel={`Rename ${layerLabel}`}
      reorderAriaLabel={intl.formatMessage(
        {
          id: "standalonePost.layers.reorder",
          defaultMessage: "Reorder {label}",
        },
        { label: layerLabel },
      )}
      hideAriaLabel={intl.formatMessage(
        {
          id: "standalonePost.layers.hide",
          defaultMessage: "Hide {label}",
        },
        { label: layerLabel },
      )}
      showAriaLabel={intl.formatMessage(
        {
          id: "standalonePost.layers.show",
          defaultMessage: "Show {label}",
        },
        { label: layerLabel },
      )}
      onSelectBlock={onSelectBlock}
      onRenameBlock={onRenameBlock}
      onToggleVisibility={onToggleVisibility}
    />
  );
}

function StandaloneLayerInspector({
  selectedBlock,
  availableFontFamilies,
  logoOptions,
  brandColors,
  isLogoUploading,
  onUploadLogo,
  onDeleteLogo,
  onUpdateBlock,
  showLayersDrawer,
  onToggleLayersDrawer,
  tab,
  caption,
  hashtags,
  onCaptionChange,
  onHashtagsChange,
  onAddLayer,
  canAddLogoLayer,
  onMoveLayerForward,
  onMoveLayerBackward,
  onMoveLayerToFront,
  onMoveLayerToBack,
  onRemoveSelectedLayer,
  canAddLayers,
  canRemoveLayer,
}: {
  selectedBlock: SocialPostSceneBlockV1 | null;
  availableFontFamilies: string[];
  logoOptions: CreativeLogoOption[];
  brandColors: string[];
  isLogoUploading: boolean;
  onUploadLogo: (file: File) => Promise<void> | void;
  onDeleteLogo: (logo: CreativeLogoOption) => Promise<void> | void;
  onUpdateBlock: (blockId: string, changes: Record<string, unknown>) => void;
  showLayersDrawer: boolean;
  onToggleLayersDrawer: () => void;
  tab: "layers" | "caption";
  caption: string;
  hashtags: string[];
  onCaptionChange: (caption: string) => void;
  onHashtagsChange: (hashtags: string[]) => void;
  onAddLayer: (kind: CreativeLayerCreateKind) => void;
  canAddLogoLayer: boolean;
  onMoveLayerForward: () => void;
  onMoveLayerBackward: () => void;
  onMoveLayerToFront: () => void;
  onMoveLayerToBack: () => void;
  onRemoveSelectedLayer: () => void;
  canAddLayers: boolean;
  canRemoveLayer: boolean;
}) {
  const intl = useIntl();
  const updateSelectedBlock = (changes: Record<string, unknown>) => {
    if (selectedBlock) {
      onUpdateBlock(selectedBlock.id, changes);
    }
  };

  return (
    <div className="min-w-0 motion-safe:animate-[inspector-fade-in_0.1s_ease-out]">
      <div className="mb-4 rounded-3xl border border-border/70 bg-background/80 p-3 shadow-[0_12px_30px_rgba(19,34,49,0.06)]">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {tab === "caption" ? (
              <Type className="h-4 w-4 text-foreground/65" aria-hidden="true" />
            ) : (
              (() => {
                const iconClass = "h-4 w-4 text-foreground/65";
                switch (selectedBlock?.type) {
                  case "text":
                    return <Type className={iconClass} aria-hidden="true" />;
                  case "image":
                    return <ImageIcon className={iconClass} aria-hidden="true" />;
                  case "logo":
                    return <ImageIcon className={iconClass} aria-hidden="true" />;
                  case "rect":
                    return <Square className={iconClass} aria-hidden="true" />;
                  case "shape":
                    return <Shapes className={iconClass} aria-hidden="true" />;
                  case "frame":
                    return <Frame className={iconClass} aria-hidden="true" />;
                  default:
                    return null;
                }
              })()
            )}
            <span className="font-display text-base font-semibold tracking-[-0.02em] text-foreground">
              {tab === "caption"
                ? "Caption"
                : (() => {
                    switch (selectedBlock?.type) {
                      case "text":
                        return "Text";
                      case "image":
                        return "Image";
                      case "logo":
                        return "Logo";
                      case "rect":
                        return "Rectangle";
                      case "shape":
                        return "Shape";
                      case "frame":
                        return "Frame";
                      default:
                        return "Canvas";
                    }
                  })()}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <AddLayerMenu
              compact
              align="right"
              disabled={!canAddLayers}
              label={intl.formatMessage({
                id: "standalonePost.addLayer",
                defaultMessage: "Add layer",
              })}
              items={[
                { label: "Text", icon: Type, onClick: () => onAddLayer("text") },
                ...(canAddLogoLayer
                  ? [{ label: "Logo", icon: ImageIcon, onClick: () => onAddLayer("logo") }]
                  : []),
                {
                  label: "Shapes",
                  icon: Shapes,
                  children: [
                    { label: "Rectangle", icon: Square, onClick: () => onAddLayer("rect") },
                    { label: "Ellipse / line", icon: Shapes, onClick: () => onAddLayer("shape") },
                  ],
                },
                { label: "Frame", icon: Frame, onClick: () => onAddLayer("frame") },
              ]}
            />
            <Button
              type="button"
              size="sm"
              variant={showLayersDrawer ? "secondary" : "outline"}
              className="h-8 w-8 rounded-full p-0"
              onClick={onToggleLayersDrawer}
              aria-label={intl.formatMessage({
                id: "standalonePost.layersDrawerToggle",
                defaultMessage: "Layers",
              })}
            >
              <BringToFront className="h-3.5 w-3.5" aria-hidden="true" />
            </Button>
          </div>
        </div>
        <p className="mt-2 text-xs leading-5 text-foreground/48">
          {tab === "caption"
            ? intl.formatMessage({
                id: "standalonePost.inspector.captionDescription",
                defaultMessage: "Refine the post copy and hashtag set.",
              })
            : selectedBlock
              ? intl.formatMessage({
                  id: "standalonePost.inspector.layerDescription",
                  defaultMessage: "Tune the selected layer with precise creative controls.",
                })
              : intl.formatMessage({
                  id: "standalonePost.inspector.selectLayerDescription",
                  defaultMessage: "Select a layer on the canvas or open Layers to start editing.",
                })}
        </p>
      </div>

      {tab === "caption" ? (
        <div className="space-y-2.5">
          <Textarea
            autoFocus
            value={caption}
            onChange={(e) => onCaptionChange(e.target.value)}
            rows={8}
            className="min-h-40 rounded-xl text-sm"
            placeholder={intl.formatMessage({
              id: "standalonePost.caption.placeholder",
              defaultMessage: "Write a caption…",
            })}
          />
          <div className="space-y-1.5">
            <span className="text-[11px] font-medium text-foreground/50">
              <FormattedMessage id="standalonePost.hashtags.title" defaultMessage="Hashtags" />
            </span>
            <div className="flex flex-wrap gap-1.5">
              {hashtags.map((token, index) => (
                <span
                  key={`${token}-${index}`}
                  className="inline-flex items-center gap-1 rounded-full bg-muted/60 px-2.5 py-1 text-xs text-foreground/70"
                >
                  <span>#{token}</span>
                  <button
                    type="button"
                    className="text-foreground/35 hover:text-destructive"
                    onClick={() => onHashtagsChange(hashtags.filter((_, i) => i !== index))}
                  >
                    <X className="h-3 w-3" aria-hidden="true" />
                  </button>
                </span>
              ))}
            </div>
            <HashtagInput hashtags={hashtags} onChange={onHashtagsChange} />
          </div>
        </div>
      ) : selectedBlock ? (
        <>
          <div className="mb-4 flex items-center gap-1 rounded-2xl border border-border/70 bg-background/75 p-1 shadow-[inset_0_1px_0_rgba(255,255,255,0.8)]">
            <button
              type="button"
              title={intl.formatMessage({
                id: "standalonePost.layerAction.bringToFront",
                defaultMessage: "Bring to front",
              })}
              onClick={onMoveLayerToFront}
              className="flex h-8 w-8 items-center justify-center rounded-xl text-foreground/50 transition-colors hover:bg-muted/70 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            >
              <BringToFront className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
            <button
              type="button"
              title={intl.formatMessage({
                id: "standalonePost.layerAction.moveForward",
                defaultMessage: "Move forward",
              })}
              onClick={onMoveLayerForward}
              className="flex h-8 w-8 items-center justify-center rounded-xl text-foreground/50 transition-colors hover:bg-muted/70 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            >
              <MoveUp className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
            <button
              type="button"
              title={intl.formatMessage({
                id: "standalonePost.layerAction.moveBackward",
                defaultMessage: "Move backward",
              })}
              onClick={onMoveLayerBackward}
              className="flex h-8 w-8 items-center justify-center rounded-xl text-foreground/50 transition-colors hover:bg-muted/70 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            >
              <MoveDown className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
            <button
              type="button"
              title={intl.formatMessage({
                id: "standalonePost.layerAction.sendToBack",
                defaultMessage: "Send to back",
              })}
              onClick={onMoveLayerToBack}
              className="flex h-8 w-8 items-center justify-center rounded-xl text-foreground/50 transition-colors hover:bg-muted/70 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            >
              <SendToBack className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
            <div className="flex-1" />
            <button
              type="button"
              title={selectedBlock.visible ? "Hide layer" : "Show layer"}
              onClick={() => updateSelectedBlock({ visible: !selectedBlock.visible })}
              className={[
                "flex h-8 w-8 items-center justify-center rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
                selectedBlock.visible
                  ? "text-foreground/50 hover:bg-muted/70 hover:text-foreground"
                  : "text-foreground/25 hover:bg-muted/70 hover:text-foreground/60",
              ].join(" ")}
            >
              {selectedBlock.visible ? (
                <Eye className="h-3.5 w-3.5" aria-hidden="true" />
              ) : (
                <EyeOff className="h-3.5 w-3.5" aria-hidden="true" />
              )}
            </button>
            <button
              type="button"
              title={intl.formatMessage({
                id: "standalonePost.layerAction.deleteLayer",
                defaultMessage: "Delete layer",
              })}
              onClick={onRemoveSelectedLayer}
              disabled={!canRemoveLayer}
              className="flex h-8 w-8 items-center justify-center rounded-xl text-foreground/35 transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-30"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
          <div className="space-y-2">
            {selectedBlock.type === "text" ? (
              <>
                <InspectorSection
                  title={intl.formatMessage({
                    id: "standalonePost.inspector.sections.typography",
                    defaultMessage: "Typography",
                  })}
                  defaultOpen={true}
                >
                  <div className="space-y-2.5">
                    <Textarea
                      value={selectedBlock.text}
                      onChange={(event) => updateSelectedBlock({ text: event.target.value })}
                      rows={3}
                      className="min-h-[6.5rem] rounded-xl text-sm"
                      placeholder={intl.formatMessage({
                        id: "standalonePost.inspector.fields.textPlaceholder",
                        defaultMessage: "Text content…",
                      })}
                    />
                    <InspectorSelectField
                      label={intl.formatMessage({
                        id: "standalonePost.inspector.fields.fontFamily",
                        defaultMessage: "Font family",
                      })}
                      value={selectedBlock.fontFamily}
                      options={availableFontFamilies}
                      previewOptions={true}
                      onChange={(value) => updateSelectedBlock({ fontFamily: value })}
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <InspectorNumberField
                        label={intl.formatMessage({
                          id: "standalonePost.inspector.fields.fontSize",
                          defaultMessage: "Size",
                        })}
                        value={selectedBlock.fontSize}
                        min={1}
                        onCommit={(fontSize) => updateSelectedBlock({ fontSize })}
                      />
                      <InspectorNumberField
                        label={intl.formatMessage({
                          id: "standalonePost.inspector.fields.lineHeight",
                          defaultMessage: "Line height",
                        })}
                        value={selectedBlock.lineHeight}
                        min={0.5}
                        max={4}
                        step={0.05}
                        onCommit={(lineHeight) => updateSelectedBlock({ lineHeight })}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label>
                        <FormattedMessage
                          id="standalonePost.inspector.fields.fontStyle"
                          defaultMessage="Style"
                        />
                      </Label>
                      <div className="flex gap-1">
                        {(["normal", "bold", "italic", "bold italic"] as SocialPostFontStyle[]).map(
                          (style) => {
                            const active = selectedBlock.fontStyle === style;
                            const label =
                              style === "normal"
                                ? "Regular"
                                : style === "bold"
                                  ? "Bold"
                                  : style === "italic"
                                    ? "Italic"
                                    : "Bold Italic";
                            return (
                              <button
                                key={style}
                                type="button"
                                title={label}
                                aria-pressed={active}
                                onClick={() => updateSelectedBlock({ fontStyle: style })}
                                className={[
                                  "flex h-8 min-w-[2rem] flex-1 items-center justify-center rounded-lg border px-1.5 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
                                  active
                                    ? "border-accent/40 bg-accent/12 text-foreground"
                                    : "border-border/60 bg-background text-foreground/55 hover:bg-muted/50",
                                ].join(" ")}
                              >
                                {style === "bold" || style === "bold italic" ? (
                                  <Bold className="h-3 w-3" aria-hidden="true" />
                                ) : null}
                                {style === "italic" || style === "bold italic" ? (
                                  <Italic className="h-3 w-3" aria-hidden="true" />
                                ) : null}
                                {style === "normal" ? (
                                  <span className="font-normal" aria-hidden="true">
                                    Aa
                                  </span>
                                ) : null}
                              </button>
                            );
                          },
                        )}
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label>
                        <FormattedMessage
                          id="standalonePost.inspector.fields.align"
                          defaultMessage="Align"
                        />
                      </Label>
                      <div className="flex gap-1">
                        {(
                          [
                            { value: "left", icon: AlignLeft, label: "Left" },
                            { value: "center", icon: AlignCenter, label: "Center" },
                            { value: "right", icon: AlignRight, label: "Right" },
                            { value: "justify", icon: AlignJustify, label: "Justify" },
                          ] as { value: SocialPostTextAlign; icon: LucideIcon; label: string }[]
                        ).map(({ value, icon: Icon, label }) => {
                          const active = selectedBlock.align === value;
                          return (
                            <button
                              key={value}
                              type="button"
                              title={label}
                              aria-pressed={active}
                              onClick={() => updateSelectedBlock({ align: value })}
                              className={[
                                "flex h-8 flex-1 items-center justify-center rounded-lg border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
                                active
                                  ? "border-accent/40 bg-accent/12 text-foreground"
                                  : "border-border/60 bg-background text-foreground/55 hover:bg-muted/50",
                              ].join(" ")}
                            >
                              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label>
                        <FormattedMessage
                          id="standalonePost.inspector.fields.verticalAlign"
                          defaultMessage="Vertical align"
                        />
                      </Label>
                      <div className="flex gap-1">
                        {(
                          [
                            { value: "top", icon: AlignStartVertical, label: "Top" },
                            { value: "middle", icon: AlignCenterVertical, label: "Middle" },
                            { value: "bottom", icon: AlignEndVertical, label: "Bottom" },
                          ] as { value: SocialPostVerticalAlign; icon: LucideIcon; label: string }[]
                        ).map(({ value, icon: Icon, label }) => {
                          const active = selectedBlock.verticalAlign === value;
                          return (
                            <button
                              key={value}
                              type="button"
                              title={label}
                              aria-pressed={active}
                              onClick={() => updateSelectedBlock({ verticalAlign: value })}
                              className={[
                                "flex h-8 flex-1 items-center justify-center rounded-lg border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
                                active
                                  ? "border-accent/40 bg-accent/12 text-foreground"
                                  : "border-border/60 bg-background text-foreground/55 hover:bg-muted/50",
                              ].join(" ")}
                            >
                              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <InspectorSliderField
                      label={intl.formatMessage({
                        id: "standalonePost.inspector.fields.letterSpacing",
                        defaultMessage: "Letter spacing",
                      })}
                      value={selectedBlock.letterSpacing}
                      min={-10}
                      max={40}
                      step={0.5}
                      valueLabel={`${selectedBlock.letterSpacing}`}
                      onCommit={(letterSpacing) => updateSelectedBlock({ letterSpacing })}
                    />
                  </div>
                </InspectorSection>
                <InspectorSection
                  title={intl.formatMessage({
                    id: "standalonePost.inspector.sections.appearance",
                    defaultMessage: "Appearance",
                  })}
                  defaultOpen={true}
                >
                  <div className="space-y-2.5">
                    <InspectorColorField
                      label={intl.formatMessage({
                        id: "standalonePost.inspector.fields.textColor",
                        defaultMessage: "Color",
                      })}
                      value={selectedBlock.fill}
                      brandColors={brandColors}
                      onCommit={(fill) => updateSelectedBlock({ fill })}
                    />
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <Label>
                          <FormattedMessage
                            id="standalonePost.inspector.fields.stroke"
                            defaultMessage="Stroke"
                          />
                        </Label>
                        <button
                          type="button"
                          onClick={() =>
                            updateSelectedBlock({
                              stroke: selectedBlock.stroke !== undefined ? undefined : "#171717",
                              strokeWidth:
                                selectedBlock.stroke !== undefined
                                  ? 0
                                  : Math.max(selectedBlock.strokeWidth ?? 0, 2),
                            })
                          }
                          className={[
                            "h-5 w-9 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
                            selectedBlock.stroke !== undefined
                              ? "bg-foreground/70"
                              : "bg-foreground/15",
                          ].join(" ")}
                          aria-label={intl.formatMessage({
                            id: "standalonePost.inspector.fields.toggleTextStroke",
                            defaultMessage: "Toggle text stroke",
                          })}
                          aria-pressed={selectedBlock.stroke !== undefined}
                        >
                          <span
                            className={[
                              "block h-4 w-4 rounded-full bg-white shadow transition-transform",
                              selectedBlock.stroke !== undefined
                                ? "translate-x-4"
                                : "translate-x-0.5",
                            ].join(" ")}
                          />
                        </button>
                      </div>
                      {selectedBlock.stroke !== undefined ? (
                        <>
                          <InspectorColorField
                            label={intl.formatMessage({
                              id: "standalonePost.inspector.fields.stroke",
                              defaultMessage: "Stroke",
                            })}
                            value={selectedBlock.stroke}
                            brandColors={brandColors}
                            onCommit={(stroke) =>
                              updateSelectedBlock({ stroke: stroke || "#171717" })
                            }
                          />
                          <InspectorSliderField
                            label={intl.formatMessage({
                              id: "standalonePost.inspector.fields.width",
                              defaultMessage: "Width",
                            })}
                            value={selectedBlock.strokeWidth ?? 0}
                            min={0}
                            max={24}
                            step={1}
                            valueLabel={`${selectedBlock.strokeWidth ?? 0}px`}
                            onCommit={(strokeWidth) => updateSelectedBlock({ strokeWidth })}
                          />
                        </>
                      ) : null}
                    </div>
                    <InspectorSliderField
                      label={intl.formatMessage({
                        id: "standalonePost.inspector.fields.opacity",
                        defaultMessage: "Opacity",
                      })}
                      value={selectedBlock.opacity}
                      min={0}
                      max={1}
                      step={0.01}
                      valueLabel={`${Math.round(selectedBlock.opacity * 100)}%`}
                      onCommit={(opacity) => updateSelectedBlock({ opacity })}
                    />
                  </div>
                </InspectorSection>
              </>
            ) : null}

            {selectedBlock.type === "image" || selectedBlock.type === "logo" ? (
              <InspectorSection
                title={intl.formatMessage({
                  id: "standalonePost.inspector.sections.image",
                  defaultMessage: "Image",
                })}
                defaultOpen={true}
              >
                <div className="space-y-3">
                  {selectedBlock.type === "logo" ? (
                    <CreativeLogoPicker
                      logos={logoOptions}
                      selectedObjectKey={selectedBlock.assetObjectKey ?? null}
                      disabled={!canAddLayers}
                      uploading={isLogoUploading}
                      onSelectLogo={(logo) =>
                        updateSelectedBlock({ assetObjectKey: logo.objectKey })
                      }
                      onDeleteLogo={onDeleteLogo}
                      onUploadLogo={onUploadLogo}
                    />
                  ) : null}
                  <InspectorFitField
                    value={selectedBlock.fit}
                    onChange={(fit) => updateSelectedBlock({ fit })}
                  />
                  <InspectorCornerRadiusField
                    value={selectedBlock.cornerRadius}
                    maxRoundRadius={Math.round(
                      Math.min(selectedBlock.width, selectedBlock.height) / 2,
                    )}
                    onChange={(cornerRadius) => updateSelectedBlock({ cornerRadius })}
                  />
                  <InspectorSliderField
                    label={intl.formatMessage({
                      id: "standalonePost.inspector.fields.opacity",
                      defaultMessage: "Opacity",
                    })}
                    value={selectedBlock.opacity}
                    min={0}
                    max={1}
                    step={0.01}
                    valueLabel={`${Math.round(selectedBlock.opacity * 100)}%`}
                    onCommit={(opacity) => updateSelectedBlock({ opacity })}
                  />
                </div>
              </InspectorSection>
            ) : null}

            {selectedBlock.type === "rect" || selectedBlock.type === "frame" ? (
              <InspectorSection
                title={intl.formatMessage({
                  id: "standalonePost.inspector.sections.shape",
                  defaultMessage: "Shape",
                })}
                defaultOpen={true}
              >
                <div className="space-y-3">
                  <InspectorColorField
                    label={intl.formatMessage({
                      id: "standalonePost.inspector.fields.fill",
                      defaultMessage: "Fill",
                    })}
                    value={selectedBlock.fill ?? "#ffffff"}
                    allowClear={selectedBlock.type === "frame"}
                    brandColors={brandColors}
                    onCommit={(fill) => updateSelectedBlock({ fill: fill || undefined })}
                  />
                  <InspectorCornerRadiusField
                    value={selectedBlock.cornerRadius}
                    maxRoundRadius={Math.round(
                      Math.min(selectedBlock.width, selectedBlock.height) / 2,
                    )}
                    onChange={(cornerRadius) => updateSelectedBlock({ cornerRadius })}
                  />
                  <InspectorSliderField
                    label={intl.formatMessage({
                      id: "standalonePost.inspector.fields.opacity",
                      defaultMessage: "Opacity",
                    })}
                    value={selectedBlock.opacity}
                    min={0}
                    max={1}
                    step={0.01}
                    valueLabel={`${Math.round(selectedBlock.opacity * 100)}%`}
                    onCommit={(opacity) => updateSelectedBlock({ opacity })}
                  />
                </div>
              </InspectorSection>
            ) : null}

            {selectedBlock.type === "shape" ? (
              <InspectorSection
                title={intl.formatMessage({
                  id: "standalonePost.inspector.sections.shape",
                  defaultMessage: "Shape",
                })}
                defaultOpen={true}
              >
                <div className="space-y-2.5">
                  <div className="space-y-1.5">
                    <Label>
                      <FormattedMessage
                        id="standalonePost.inspector.fields.shapeType"
                        defaultMessage="Type"
                      />
                    </Label>
                    <div className="grid grid-cols-2 gap-1">
                      {(["ellipse", "line"] as SocialPostShapeType[]).map((shapeType) => {
                        const active = selectedBlock.shapeType === shapeType;
                        return (
                          <button
                            key={shapeType}
                            type="button"
                            aria-pressed={active}
                            onClick={() => {
                              const next = applyCreativeShapePreset(selectedBlock, shapeType);
                              updateSelectedBlock({
                                shapeType: next.shapeType,
                                points: next.points,
                                closed: next.closed,
                                tension: next.tension,
                                sides: next.sides,
                                radius: next.radius,
                                innerRadius: next.innerRadius,
                                outerRadius: next.outerRadius,
                                angle: next.angle,
                                clockwise: next.clockwise,
                                pathData: next.pathData,
                                fill: next.fill,
                                stroke: next.stroke,
                                strokeWidth: next.strokeWidth,
                                lineCap: next.lineCap,
                                lineJoin: next.lineJoin,
                              });
                            }}
                            className={[
                              "flex h-8 items-center justify-center rounded-lg border text-xs font-medium capitalize transition-colors",
                              active
                                ? "border-accent/40 bg-accent/12 text-foreground"
                                : "border-border/60 bg-background text-foreground/55 hover:bg-muted/50",
                            ].join(" ")}
                          >
                            {shapeType}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {selectedBlock.shapeType !== "line" ? (
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <Label>
                          <FormattedMessage
                            id="standalonePost.inspector.fields.fill"
                            defaultMessage="Fill"
                          />
                        </Label>
                        <button
                          type="button"
                          onClick={() =>
                            updateSelectedBlock({
                              fill: selectedBlock.fill !== undefined ? undefined : "#171717",
                            })
                          }
                          className={[
                            "h-5 w-9 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
                            selectedBlock.fill !== undefined
                              ? "bg-foreground/70"
                              : "bg-foreground/15",
                          ].join(" ")}
                          aria-pressed={selectedBlock.fill !== undefined}
                        >
                          <span
                            className={[
                              "block h-4 w-4 rounded-full bg-white shadow transition-transform",
                              selectedBlock.fill !== undefined
                                ? "translate-x-4"
                                : "translate-x-0.5",
                            ].join(" ")}
                          />
                        </button>
                      </div>
                      {selectedBlock.fill !== undefined ? (
                        <InspectorColorField
                          label=""
                          value={selectedBlock.fill}
                          brandColors={brandColors}
                          onCommit={(fill) => updateSelectedBlock({ fill: fill || "#171717" })}
                        />
                      ) : null}
                    </div>
                  ) : null}
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <Label>
                        <FormattedMessage
                          id="standalonePost.inspector.fields.stroke"
                          defaultMessage="Stroke"
                        />
                      </Label>
                      <button
                        type="button"
                        onClick={() =>
                          updateSelectedBlock({
                            stroke: selectedBlock.stroke !== undefined ? undefined : "#171717",
                            strokeWidth:
                              selectedBlock.stroke !== undefined
                                ? 0
                                : Math.max(selectedBlock.strokeWidth, 2),
                          })
                        }
                        className={[
                          "h-5 w-9 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
                          selectedBlock.stroke !== undefined
                            ? "bg-foreground/70"
                            : "bg-foreground/15",
                        ].join(" ")}
                        aria-pressed={selectedBlock.stroke !== undefined}
                      >
                        <span
                          className={[
                            "block h-4 w-4 rounded-full bg-white shadow transition-transform",
                            selectedBlock.stroke !== undefined
                              ? "translate-x-4"
                              : "translate-x-0.5",
                          ].join(" ")}
                        />
                      </button>
                    </div>
                    {selectedBlock.stroke !== undefined ? (
                      <>
                        <InspectorColorField
                          label=""
                          value={selectedBlock.stroke}
                          brandColors={brandColors}
                          onCommit={(stroke) =>
                            updateSelectedBlock({ stroke: stroke || "#171717" })
                          }
                        />
                        <InspectorSliderField
                          label={intl.formatMessage({
                            id: "standalonePost.inspector.fields.width",
                            defaultMessage: "Width",
                          })}
                          value={selectedBlock.strokeWidth}
                          min={0}
                          max={24}
                          step={1}
                          valueLabel={`${selectedBlock.strokeWidth}px`}
                          onCommit={(strokeWidth) => updateSelectedBlock({ strokeWidth })}
                        />
                      </>
                    ) : null}
                  </div>
                  <InspectorSliderField
                    label={intl.formatMessage({
                      id: "standalonePost.inspector.fields.opacity",
                      defaultMessage: "Opacity",
                    })}
                    value={selectedBlock.opacity}
                    min={0}
                    max={1}
                    step={0.01}
                    valueLabel={`${Math.round(selectedBlock.opacity * 100)}%`}
                    onCommit={(opacity) => updateSelectedBlock({ opacity })}
                  />
                </div>
              </InspectorSection>
            ) : null}

            {selectedBlock.type !== "text" &&
            selectedBlock.type !== "image" &&
            selectedBlock.type !== "logo" &&
            selectedBlock.type !== "rect" &&
            selectedBlock.type !== "frame" &&
            selectedBlock.type !== "shape" ? (
              <InspectorSection
                title={intl.formatMessage({
                  id: "standalonePost.inspector.sections.appearance",
                  defaultMessage: "Appearance",
                })}
                defaultOpen={true}
              >
                <InspectorSliderField
                  label={intl.formatMessage({
                    id: "standalonePost.inspector.fields.opacity",
                    defaultMessage: "Opacity",
                  })}
                  value={selectedBlock.opacity}
                  min={0}
                  max={1}
                  step={0.01}
                  valueLabel={`${Math.round(selectedBlock.opacity * 100)}%`}
                  onCommit={(opacity) => updateSelectedBlock({ opacity })}
                />
              </InspectorSection>
            ) : null}

            <InspectorSection
              title={intl.formatMessage({
                id: "standalonePost.inspector.sections.transform",
                defaultMessage: "Transform",
              })}
              defaultOpen={true}
            >
              <div className="space-y-2">
                {selectedBlock.type === "rect" ? (
                  <RectangleSizePresetControl
                    width={selectedBlock.width}
                    height={selectedBlock.height}
                    onUpdate={updateSelectedBlock}
                    squareLabel={intl.formatMessage({
                      id: "standalonePost.inspector.presets.square",
                      defaultMessage: "Square",
                    })}
                    rectangleLabel={intl.formatMessage({
                      id: "standalonePost.inspector.presets.rectangle",
                      defaultMessage: "Rectangle",
                    })}
                  />
                ) : null}
                <InspectorSliderField
                  label={intl.formatMessage({
                    id: "standalonePost.inspector.fields.rotation",
                    defaultMessage: "Rotation",
                  })}
                  value={selectedBlock.rotation}
                  min={-180}
                  max={180}
                  step={1}
                  valueLabel={`${Math.round(selectedBlock.rotation)}°`}
                  onCommit={(rotation) => updateSelectedBlock({ rotation })}
                />
                <InspectorAdvancedGeometry
                  block={selectedBlock}
                  onUpdate={(changes) => updateSelectedBlock(changes)}
                />
              </div>
            </InspectorSection>
          </div>
        </>
      ) : (
        <div className="px-2 py-10 text-center text-sm text-foreground/35">
          <FormattedMessage
            id="standalonePost.inspector.empty"
            defaultMessage="Select a layer to edit its properties."
          />
        </div>
      )}
    </div>
  );
}

function HashtagInput({
  hashtags,
  onChange,
}: {
  hashtags: string[];
  onChange: (hashtags: string[]) => void;
}) {
  const intl = useIntl();
  return (
    <CreativePostHashtagInput
      hashtags={hashtags}
      placeholder={intl.formatMessage({
        id: "standalonePost.hashtag.placeholder",
        defaultMessage: "#hashtag",
      })}
      addLabel={<FormattedMessage id="standalonePost.hashtag.add" defaultMessage="Add" />}
      onChange={onChange}
    />
  );
}

function InspectorAdvancedGeometry({
  block,
  onUpdate,
}: {
  block: SocialPostSceneBlockV1;
  onUpdate: (changes: Record<string, unknown>) => void;
}) {
  const intl = useIntl();
  return (
    <CreativeInspectorAdvancedGeometry
      block={block}
      title={intl.formatMessage({
        id: "standalonePost.inspector.advancedGeometry",
        defaultMessage: "Advanced geometry",
      })}
      labels={{
        x: intl.formatMessage({ id: "standalonePost.inspector.fields.x", defaultMessage: "X" }),
        y: intl.formatMessage({ id: "standalonePost.inspector.fields.y", defaultMessage: "Y" }),
        width: intl.formatMessage({
          id: "standalonePost.inspector.fields.width",
          defaultMessage: "Width",
        }),
        height: intl.formatMessage({
          id: "standalonePost.inspector.fields.height",
          defaultMessage: "Height",
        }),
      }}
      minSize={8}
      onUpdate={onUpdate}
    />
  );
}

function InspectorColorField({
  label,
  value,
  onCommit,
  allowClear = false,
  brandColors = [],
}: {
  label: string;
  value: string;
  onCommit: (value: string) => void;
  allowClear?: boolean;
  brandColors?: string[];
}) {
  const intl = useIntl();
  return (
    <CreativeInspectorColorField
      label={label}
      value={value}
      allowClear={allowClear}
      brandColors={brandColors}
      clearLabel={intl.formatMessage({
        id: "standalonePost.inspector.clear",
        defaultMessage: "Clear",
      })}
      colorPickerLabels={{
        brandPalette: intl.formatMessage({
          id: "standalonePost.inspector.brandPalette",
          defaultMessage: "Brand palette",
        }),
        hexInput: intl.formatMessage({
          id: "standalonePost.inspector.hexColor",
          defaultMessage: "Hex color",
        }),
        hueSlider: intl.formatMessage({
          id: "standalonePost.inspector.hue",
          defaultMessage: "Hue",
        }),
        openPicker: intl.formatMessage({
          id: "standalonePost.inspector.openColorPicker",
          defaultMessage: "Open color picker",
        }),
        useBrandColor: (color) =>
          intl.formatMessage(
            {
              id: "standalonePost.inspector.useBrandColor",
              defaultMessage: "Use brand color {color}",
            },
            { color },
          ),
      }}
      onCommit={onCommit}
    />
  );
}

function InspectorFitField({
  value,
  onChange,
}: {
  value: ImageFitValue;
  onChange: (value: ImageFitValue) => void;
}) {
  const intl = useIntl();
  return (
    <CreativeInspectorFitField
      intl={intl}
      label={
        <FormattedMessage id="standalonePost.inspector.fields.fit" defaultMessage="Image fit" />
      }
      value={value}
      onChange={onChange}
    />
  );
}

function InspectorCornerRadiusField({
  value,
  maxRoundRadius,
  onChange,
}: {
  value: number;
  maxRoundRadius: number;
  onChange: (value: number) => void;
}) {
  const intl = useIntl();
  return (
    <CreativeInspectorCornerRadiusField
      label={
        <FormattedMessage
          id="standalonePost.inspector.fields.cornerRadius"
          defaultMessage="Corner radius"
        />
      }
      customLabel={intl.formatMessage({
        id: "standalonePost.inspector.cornerRadius.custom",
        defaultMessage: "Custom",
      })}
      optionLabels={{
        square: intl.formatMessage({
          id: "standalonePost.inspector.cornerRadius.square",
          defaultMessage: "Square",
        }),
        soft: intl.formatMessage({
          id: "standalonePost.inspector.cornerRadius.soft",
          defaultMessage: "Soft",
        }),
        round: intl.formatMessage({
          id: "standalonePost.inspector.cornerRadius.round",
          defaultMessage: "Round",
        }),
      }}
      value={value}
      maxRoundRadius={maxRoundRadius}
      onChange={onChange}
    />
  );
}

function getLayerLabel(block: SocialPostSceneBlockV1, intl: ReturnType<typeof useIntl>) {
  const customName = getCustomLayerName(block);
  if (customName && block.type !== "text") {
    return customName;
  }

  switch (block.type) {
    case "text":
      return truncateLayerLabel(block.text);
    case "image":
      return intl.formatMessage({
        id: "standalonePost.layerLabels.image",
        defaultMessage: "Image",
      });
    case "logo":
      return intl.formatMessage({ id: "standalonePost.layerLabels.logo", defaultMessage: "Logo" });
    case "rect":
      return intl.formatMessage({
        id: "standalonePost.layerLabels.shapeBackground",
        defaultMessage: "Shape background",
      });
    case "frame":
      return intl.formatMessage({
        id: "standalonePost.layerLabels.frame",
        defaultMessage: "Frame",
      });
    case "shape":
      return intl.formatMessage(
        { id: "standalonePost.layerLabels.shape", defaultMessage: "{shapeType} shape" },
        { shapeType: `${block.shapeType[0]?.toUpperCase() ?? "S"}${block.shapeType.slice(1)}` },
      );
    default:
      return intl.formatMessage({
        id: "standalonePost.layerLabels.layer",
        defaultMessage: "Layer",
      });
  }
}

function getEditableLayerName(block: SocialPostSceneBlockV1, intl: ReturnType<typeof useIntl>) {
  return getCustomLayerName(block) ?? getLayerLabel(block, intl);
}
